/** Rooms (docs/API.md "Rooms", docs/ROOMS.md): rooms, members, messages, moderation, search and the live streams. */
import { z } from 'zod'
import { RoomAgentPolicy, RoomAgentProposal, RoomAgentProposalParams, RoomAgentWatch, RoomAgentWatchParams } from '../participation.js'
import {
  AddRoomAgentBody,
  CreateRoomBody,
  CreateRoomInviteBody,
  EditRoomMessageBody,
  JoinRoomBody,
  ListRoomMembersQuery,
  ListRoomsQuery,
  ModerateRoomMemberBody,
  PatchRoomBody,
  PostRoomMessageBody,
  RoomDetail,
  RoomInvite,
  RoomInviteCodeParams,
  RoomInviteParams,
  RoomInvitePreview,
  RoomInvites,
  RoomLeft,
  RoomListStreamEvent,
  RoomMedia,
  RoomMember,
  RoomMemberPage,
  RoomMemberParams,
  RoomMentionCandidates,
  RoomMentionsQuery,
  RoomMessage,
  RoomMessagePage,
  RoomMessageParams,
  RoomMessagesQuery,
  RoomMuteBody,
  RoomPage,
  RoomParams,
  RoomPins,
  RoomReactionBody,
  RoomReactionsOf,
  RoomReadBody,
  RoomReadState,
  RoomSearchPage,
  RoomSearchQuery,
  RoomSlugParams,
  RoomStreamEvent,
  RoomStreamQuery,
  RoomTyping,
  SetRoomRoleBody,
} from '../rooms.js'
import { defineRoute, type RouteDef } from './define.js'

const NoBody = z.object({}).strict()

export const roomRoutes = {
  setRoomAgentPolicy: defineRoute({method:'POST',path:'/rooms/:id/members/:memberId/participation',auth:'session',params:RoomMemberParams,body:RoomAgentPolicy,response:RoomMember,summary:'Set scoped participation and creator-paid budget (room and agent owner)'}),
  getRoomAgentProposal: defineRoute({method:'GET',path:'/rooms/:id/agent-proposals/:proposalId',auth:'session',params:RoomAgentProposalParams,response:RoomAgentProposal,summary:'Read a room agent proposal (current members)'}),
  decideRoomAgentProposal: defineRoute({method:'POST',path:'/rooms/:id/agent-proposals/:proposalId/decide',auth:'session',params:RoomAgentProposalParams,body:z.object({decision:z.enum(['accept','reject'])}).strict(),response:RoomAgentProposal,summary:'Approve a room watch or continue research in the owner’s private desk'}),
  listRoomAgentWatches: defineRoute({method:'GET',path:'/rooms/:id/agent-watches',auth:'session',params:RoomParams,response:z.array(RoomAgentWatch),summary:'The room’s bounded thesis watches'}),
  stopRoomAgentWatch: defineRoute({method:'POST',path:'/rooms/:id/agent-watches/:watchId/stop',auth:'session',params:RoomAgentWatchParams,response:RoomAgentWatch,summary:'Stop a room watch (owner)'}),
  // Rooms ---------------------------------------------------------------------------------------------------------
  listRooms: defineRoute({
    method: 'GET',
    path: '/rooms',
    auth: 'session',
    summary: 'The caller’s rooms, most recently active first, or public rooms to discover',
    query: ListRoomsQuery,
    response: RoomPage,
  }),
  createRoom: defineRoute({
    method: 'POST',
    path: '/rooms',
    auth: 'session',
    summary: 'Make a room (the caller owns it), or open a direct message (an existing one answers 200)',
    body: CreateRoomBody,
    status: 201,
    response: RoomDetail,
  }),
  getRoomBySlug: defineRoute({
    method: 'GET',
    path: '/rooms/by-slug/:slug',
    auth: 'session',
    summary: 'A room by its address; a non-member of a room that is not public sees its preview only (`me` null, no pins)',
    params: RoomSlugParams,
    response: RoomDetail,
  }),
  getRoomCard: defineRoute({
    method: 'GET',
    path: '/rooms/:id/card.png',
    auth: 'public',
    summary: 'A room’s invite card (1200×630 PNG), the platform’s drawing; a private room, a direct message or an archived room has none (404)',
    params: RoomParams,
    responseType: 'png',
    response: z.string(),
  }),
  getRoom: defineRoute({
    method: 'GET',
    path: '/rooms/:id',
    auth: 'session',
    summary: 'A room with the caller’s membership and its pins',
    params: RoomParams,
    response: RoomDetail,
  }),
  patchRoom: defineRoute({
    method: 'PATCH',
    path: '/rooms/:id',
    auth: 'session',
    summary: 'Rename the room or change its topic, picture, address, entry rule or settings (owner or admin; the entry rule is the owner’s)',
    params: RoomParams,
    body: PatchRoomBody,
    response: RoomDetail,
  }),
  archiveRoom: defineRoute({
    method: 'POST',
    path: '/rooms/:id/archive',
    auth: 'session',
    summary: 'Archive the room: readable, nothing new written (owner)',
    params: RoomParams,
    body: NoBody,
    response: RoomDetail,
  }),
  unarchiveRoom: defineRoute({
    method: 'POST',
    path: '/rooms/:id/unarchive',
    auth: 'session',
    summary: 'Open an archived room again (owner)',
    params: RoomParams,
    body: NoBody,
    response: RoomDetail,
  }),
  uploadRoomMedia: defineRoute({
    method: 'POST',
    path: '/rooms/:id/media',
    auth: 'session',
    summary: 'Upload one image for a `media` message (multipart field `file`; png, jpeg, gif or webp up to 2 MiB; members)',
    params: RoomParams,
    bodyType: 'multipart',
    status: 201,
    response: RoomMedia,
  }),

  // Membership ----------------------------------------------------------------------------------------------------
  listRoomMembers: defineRoute({
    method: 'GET',
    path: '/rooms/:id/members',
    auth: 'session',
    summary: 'The room’s members, owner and admins first (members of the room; a public room’s list is open to everyone signed in)',
    params: RoomParams,
    query: ListRoomMembersQuery,
    response: RoomMemberPage,
  }),
  joinRoom: defineRoute({
    method: 'POST',
    path: '/rooms/:id/join',
    auth: 'session',
    summary: 'Join by the room’s entry rule, or with an invite code (joining again answers the room unchanged)',
    params: RoomParams,
    body: JoinRoomBody,
    response: RoomDetail,
  }),
  leaveRoom: defineRoute({
    method: 'POST',
    path: '/rooms/:id/leave',
    auth: 'session',
    summary: 'Leave the room (not the owner; leaving again is fine)',
    params: RoomParams,
    body: NoBody,
    response: RoomLeft,
  }),
  listRoomInvites: defineRoute({
    method: 'GET',
    path: '/rooms/:id/invites',
    auth: 'session',
    summary: 'The room’s open invites (those the caller may make)',
    params: RoomParams,
    response: RoomInvites,
  }),
  createRoomInvite: defineRoute({
    method: 'POST',
    path: '/rooms/:id/invites',
    auth: 'session',
    summary: 'Make an invite: a code anyone may use, or a direct invite to one person, who is notified',
    params: RoomParams,
    body: CreateRoomInviteBody,
    status: 201,
    response: RoomInvite,
  }),
  revokeRoomInvite: defineRoute({
    method: 'DELETE',
    path: '/rooms/:id/invites/:code',
    auth: 'session',
    summary: 'Withdraw an invite (its maker, the owner or an admin)',
    params: RoomInviteParams,
    response: RoomInvite,
  }),
  previewRoomInvite: defineRoute({
    method: 'GET',
    path: '/room-invites/:code',
    auth: 'session',
    summary: 'What an invite opens and whether the caller may still join with it',
    params: RoomInviteCodeParams,
    response: RoomInvitePreview,
  }),
  addRoomAgent: defineRoute({
    method: 'POST',
    path: '/rooms/:id/agents',
    auth: 'session',
    summary: 'Add one of the caller’s own agents as a member (the room’s owner; the room must take agents)',
    params: RoomParams,
    body: AddRoomAgentBody,
    status: 201,
    response: RoomMember,
  }),
  setRoomMemberRole: defineRoute({
    method: 'POST',
    path: '/rooms/:id/members/:memberId/role',
    auth: 'session',
    summary: 'Make a person an admin or a member again (owner)',
    params: RoomMemberParams,
    body: SetRoomRoleBody,
    response: RoomMember,
  }),
  kickRoomMember: defineRoute({
    method: 'POST',
    path: '/rooms/:id/members/:memberId/kick',
    auth: 'session',
    summary: 'Remove a member (owner or admin; an admin only removes members); they may join again by the rule',
    params: RoomMemberParams,
    body: ModerateRoomMemberBody,
    response: RoomMember,
  }),
  banRoomMember: defineRoute({
    method: 'POST',
    path: '/rooms/:id/members/:memberId/ban',
    auth: 'session',
    summary: 'Remove a member and keep them out (owner or admin; an admin only bans members)',
    params: RoomMemberParams,
    body: ModerateRoomMemberBody,
    response: RoomMember,
  }),
  unbanRoomMember: defineRoute({
    method: 'POST',
    path: '/rooms/:id/members/:memberId/unban',
    auth: 'session',
    summary: 'Lift a ban (owner or admin); the person may join again by the rule',
    params: RoomMemberParams,
    body: NoBody,
    response: RoomMember,
  }),

  // Messages ------------------------------------------------------------------------------------------------------
  listRoomMessages: defineRoute({
    method: 'GET',
    path: '/rooms/:id/messages',
    auth: 'session',
    summary: 'Messages newest first, paged by seq (members; a public room is readable by everyone signed in)',
    params: RoomParams,
    query: RoomMessagesQuery,
    response: RoomMessagePage,
  }),
  postRoomMessage: defineRoute({
    method: 'POST',
    path: '/rooms/:id/messages',
    auth: 'session',
    summary: 'Write a message (members); idempotent by `Idempotency-Key`',
    params: RoomParams,
    body: PostRoomMessageBody,
    status: 201,
    response: RoomMessage,
  }),
  editRoomMessage: defineRoute({
    method: 'PATCH',
    path: '/rooms/:id/messages/:seq',
    auth: 'session',
    summary: 'Edit the body of one’s own message',
    params: RoomMessageParams,
    body: EditRoomMessageBody,
    response: RoomMessage,
  }),
  deleteRoomMessage: defineRoute({
    method: 'DELETE',
    path: '/rooms/:id/messages/:seq',
    auth: 'session',
    summary: 'Delete a message: one’s own, or any as owner or admin (the body is cleared, the audit kept; deleting again is fine)',
    params: RoomMessageParams,
    response: RoomMessage,
  }),
  reactRoomMessage: defineRoute({
    method: 'POST',
    path: '/rooms/:id/messages/:seq/react',
    auth: 'session',
    summary: 'Add the caller’s reaction (the same one again is fine)',
    params: RoomMessageParams,
    body: RoomReactionBody,
    response: RoomReactionsOf,
  }),
  unreactRoomMessage: defineRoute({
    method: 'POST',
    path: '/rooms/:id/messages/:seq/unreact',
    auth: 'session',
    summary: 'Take the caller’s reaction back (one that is not there is fine)',
    params: RoomMessageParams,
    body: RoomReactionBody,
    response: RoomReactionsOf,
  }),
  pinRoomMessage: defineRoute({
    method: 'POST',
    path: '/rooms/:id/messages/:seq/pin',
    auth: 'session',
    summary: 'Pin a message (owner or admin; pinning again is fine)',
    params: RoomMessageParams,
    body: NoBody,
    response: RoomMessage,
  }),
  unpinRoomMessage: defineRoute({
    method: 'POST',
    path: '/rooms/:id/messages/:seq/unpin',
    auth: 'session',
    summary: 'Unpin a message (owner or admin)',
    params: RoomMessageParams,
    body: NoBody,
    response: RoomMessage,
  }),
  listRoomPins: defineRoute({
    method: 'GET',
    path: '/rooms/:id/pins',
    auth: 'session',
    summary: 'The pinned messages, newest pin first',
    params: RoomParams,
    response: RoomPins,
  }),
  roomMentionCandidates: defineRoute({
    method: 'GET',
    path: '/rooms/:id/mentions',
    auth: 'session',
    summary: 'Who and what a member may mention: the room’s people and agents, and tokens by symbol',
    params: RoomParams,
    query: RoomMentionsQuery,
    response: RoomMentionCandidates,
  }),
  searchRoom: defineRoute({
    method: 'GET',
    path: '/rooms/:id/search',
    auth: 'session',
    summary: 'Messages of one room matching words (members)',
    params: RoomParams,
    query: RoomSearchQuery,
    response: RoomSearchPage,
  }),
  searchRooms: defineRoute({
    method: 'GET',
    path: '/rooms/search',
    auth: 'session',
    summary: 'Messages matching words across the rooms the caller is a member of',
    query: RoomSearchQuery,
    response: RoomSearchPage,
  }),
  markRoomRead: defineRoute({
    method: 'POST',
    path: '/rooms/:id/read',
    auth: 'session',
    summary: 'Mark the room read up to a seq (it never moves back)',
    params: RoomParams,
    body: RoomReadBody,
    response: RoomReadState,
  }),
  muteRoom: defineRoute({
    method: 'POST',
    path: '/rooms/:id/mute',
    auth: 'session',
    summary: 'Mute the room until a time (null unmutes) and/or set what the caller hears from it',
    params: RoomParams,
    body: RoomMuteBody,
    response: RoomDetail,
  }),
  roomTyping: defineRoute({
    method: 'POST',
    path: '/rooms/:id/typing',
    auth: 'session',
    summary: 'Tell the room the caller is typing (never stored; holds 10 s)',
    params: RoomParams,
    body: NoBody,
    status: 202,
    response: RoomTyping,
  }),

  // Live ----------------------------------------------------------------------------------------------------------
  streamRoom: defineRoute({
    method: 'GET',
    path: '/rooms/:id/stream',
    auth: 'session',
    summary: 'SSE of one room: messages, edits, reactions, pins, members, typing and presence; resume by Last-Event-ID or ?after=',
    params: RoomParams,
    query: RoomStreamQuery,
    response: RoomStreamEvent,
    stream: true,
  }),
  streamRoomList: defineRoute({
    method: 'GET',
    path: '/rooms/stream',
    auth: 'session',
    summary: 'SSE of the caller’s room list: a room whenever it changes (new message, unread counts), and removals',
    response: RoomListStreamEvent,
    stream: true,
  }),
} as const satisfies Record<string, RouteDef>
